import { cn } from "@/shared/utils";
import type { ComponentProps } from "react";

function clamp(value: number, min: number, max: number): number {
    return Math.max(Math.min(value, Math.max(min, max)), Math.min(min, max));
}

type ProgressCircleProps = ComponentProps<"svg"> & {
    value: number;
    size?: number;
    strokeWidth?: number;
    showCheck?: boolean;
    showExclamation?: boolean;
};

const DEFAULT_SIZE = 80;
const DEFAULT_STROKE_WIDTH = 10;

function ProgressCircle({
    value,
    size = DEFAULT_SIZE,
    strokeWidth = DEFAULT_STROKE_WIDTH,
    showCheck,
    showExclamation,
    ...props
}: ProgressCircleProps) {
    // 0.001 is to show a small green line which represents 0%
    const normalizedValue = clamp(value || 0.01, 0, 1);
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const progress = normalizedValue * circumference;
    const center = size / 2;
    const innerRadius = radius - strokeWidth / 2;

    return (
        <div data-slot="progress-circle" className="text-chart-success relative" style={{ width: size, height: size }}>
            <svg viewBox={`0 0 ${size} ${size}`} aria-hidden="true" {...props}>
                {(showCheck || showExclamation) && <circle cx={center} cy={center} r={innerRadius} fill="currentColor" opacity={0.25} />}
                {showExclamation && <ExclamationMark />}
                {showCheck && <Checkmark />}
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    className="opacity-25"
                />
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference - progress}
                    transform={`rotate(-90 ${center} ${center})`}
                    className="transition-[stroke-dashoffset] duration-300"
                />
            </svg>
        </div>
    );
}

ProgressCircle.displayName = "ProgressCircle";

type ProgressCircleWithLabelProps = {
    total: number;
    current: number;
    label?: string;
    unit?: string;
    className?: string;
    button?: React.ReactNode;
    type?: "default" | "check" | "exclamation";
};

function ProgressCircleWithLabel({
    total,
    current,
    label = "Voltooid",
    unit,
    className,
    button,
    type = "default",
}: Readonly<ProgressCircleWithLabelProps>) {
    const fraction = total > 0 ? current / total : 0;
    const colorClass = type === "exclamation" ? "text-chart-warning" : "text-chart-success";

    return (
        <figure
            data-slot="progress-circle-with-label"
            className={cn("flex items-center gap-3", className)}
            data-testid="progress"
        >
            <ProgressCircle value={fraction} showCheck={type === "check"} showExclamation={type === "exclamation"} className={colorClass} />
            <figcaption className="flex h-full flex-col justify-center border-l pl-4 font-medium">
                <span className="text-content-subtle text-s" data-testid="progress-status">
                    {label}
                </span>
                <div className="text-content-emphasis flex items-end gap-2 pt-2" data-testid="progress-value">
                    <span className="text-xxxl [line-height:1]">
                        {current}/{total}
                    </span>
                    {unit && <span className="text-s">{unit}</span>}
                </div>
                {button && <div className="pt-4">{button}</div>}
            </figcaption>
        </figure>
    );
}

ProgressCircleWithLabel.displayName = "ProgressCircleWithLabel";

function Checkmark() {
    return (
        <path
            d="M53.5298 31.2883C54.2413 30.4434 54.1332 29.1817 53.2883 28.4702C52.4434 27.7587 51.1817 27.8669 50.4702 28.7118L35.9591 45.9437L29.4948 38.6713C28.761 37.8458 27.4969 37.7714 26.6713 38.5052C25.8457 39.2391 25.7714 40.5032 26.5052 41.3288L36.0409 52.0564L53.5298 31.2883Z"
            fill="currentColor"
        />
    );
}

function ExclamationMark() {
    return (
        <g>
            <path
                d="M36.0424 44.2744L36.0091 24.2777C36.0054 22.0678 37.7953 20.2744 40.0046 20.2744C42.2113 20.2744 44.0002 22.0638 44.0002 24.2711V44.2744H36.0424Z"
                fill="currentColor"
            />
            <path
                d="M35.0025 54.2744C35.0025 57.0244 37.2519 59.2744 40.0011 59.2744C42.7504 59.2744 44.9998 57.0244 44.9998 54.2744C44.9998 51.5244 42.7504 49.2744 40.0011 49.2744C37.2519 49.2744 35.0025 51.5244 35.0025 54.2744Z"
                fill="currentColor"
            />
        </g>
    );
}

export { ProgressCircle, ProgressCircleWithLabel };
